import { useState, useEffect } from 'react';

interface DeviceInfo {
  isIPad: boolean;
  isMobile: boolean;
  isTablet: boolean;
  screenWidth: number;
  screenHeight: number;
  orientation: 'portrait' | 'landscape';
  isIPadPro: boolean;
  devicePixelRatio: number;
}

export const useDeviceDetection = (): DeviceInfo => {
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    isIPad: false,
    isMobile: false,
    isTablet: false,
    screenWidth: 0,
    screenHeight: 0,
    orientation: 'portrait',
    isIPadPro: false,
    devicePixelRatio: 1
  });

  useEffect(() => {
    const updateDeviceInfo = () => {
      const userAgent = navigator.userAgent;
      const screenWidth = window.innerWidth;
      const screenHeight = window.innerHeight;
      const devicePixelRatio = window.devicePixelRatio || 1;
      
      // Detect iPad (including iPad Pro with desktop user agent)
      const isIPad = /iPad/.test(userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                    (/Macintosh/.test(userAgent) && 'ontouchend' in document);
      
      // Detect iPad Pro specifically
      const isIPadPro = isIPad && (
        (screenWidth >= 1024 && screenHeight >= 1366) || // 12.9" iPad Pro
        (screenWidth >= 834 && screenHeight >= 1194) ||  // 11" iPad Pro
        (screenWidth >= 1366 && screenHeight >= 1024) || // 12.9" iPad Pro landscape
        (screenWidth >= 1194 && screenHeight >= 834)     // 11" iPad Pro landscape
      );
      
      // Detect mobile devices
      const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Detect tablets (including iPad)
      const isTablet = isIPad || 
                      (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) ||
                      (screenWidth >= 768 && screenWidth <= 1024);
      
      // Determine orientation
      const orientation = screenWidth > screenHeight ? 'landscape' : 'portrait';
      
      setDeviceInfo({
        isIPad,
        isMobile,
        isTablet,
        screenWidth,
        screenHeight,
        orientation,
        isIPadPro,
        devicePixelRatio
      });
    };

    // Initial detection
    updateDeviceInfo();

    // Listen for resize/orientation changes with debouncing
    let timeoutId: NodeJS.Timeout;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(updateDeviceInfo, 100);
    };

    window.addEventListener('resize', debouncedUpdate);
    window.addEventListener('orientationchange', () => {
      // Add a small delay for orientation change to complete
      setTimeout(updateDeviceInfo, 200);
    });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', debouncedUpdate);
      window.removeEventListener('orientationchange', updateDeviceInfo);
    };
  }, []);

  return deviceInfo;
};